import * as THREE from 'three';

export type InteractionEffect = 'water-impact' | 'revive' | 'eat' | 'pickup' | 'throw-impact' | 'thorn-hit';

type Particle = {
  active: boolean;
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  life: number;
  maxLife: number;
  size: number;
  color: THREE.Color;
};

export class VfxSystem {
  readonly root = new THREE.Group();

  private readonly dropletGeometry = new THREE.IcosahedronGeometry(0.075, 0);
  private readonly burstGeometry = new THREE.IcosahedronGeometry(0.075, 0);
  private readonly sprayJetGeometry = new THREE.CylinderGeometry(0.08, 0.18, 1, 8, 1, true);
  private readonly dropletMaterial = new THREE.MeshBasicMaterial({ color: 0x77dce4, transparent: true, opacity: 0.88 });
  private readonly sprayJetMaterial = new THREE.MeshBasicMaterial({
    color: 0x6de5ef,
    transparent: true,
    opacity: 0.5,
    depthWrite: false,
  });
  private readonly burstMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff, vertexColors: true, transparent: true, opacity: 0.92 });
  private readonly sprayJet = new THREE.Mesh(this.sprayJetGeometry, this.sprayJetMaterial);
  private readonly droplets = new THREE.InstancedMesh(this.dropletGeometry, this.dropletMaterial, 96);
  private readonly bursts = new THREE.InstancedMesh(this.burstGeometry, this.burstMaterial, 64);
  private readonly dropletParticles = createParticlePool(96);
  private readonly burstParticles = createParticlePool(64);
  private readonly matrix = new THREE.Matrix4();
  private readonly quaternion = new THREE.Quaternion();
  private readonly jetQuaternion = new THREE.Quaternion();
  private readonly scale = new THREE.Vector3();
  private sprayAccumulator = 0;
  private nextDroplet = 0;
  private nextBurst = 0;
  private sprayJetLife = 0;

  constructor() {
    this.root.name = 'vfxSystem';
    this.droplets.name = 'pooledWaterDroplets';
    this.bursts.name = 'pooledInteractionBurst';
    this.sprayJet.name = 'trunkWaterJet';
    this.sprayJet.visible = false;
    this.sprayJet.renderOrder = 2;
    this.droplets.frustumCulled = false;
    this.bursts.frustumCulled = false;
    this.root.add(this.sprayJet, this.droplets, this.bursts);
    this.hideInactiveInstances(this.droplets, this.dropletParticles);
    this.hideInactiveInstances(this.bursts, this.burstParticles);
  }

  emitSpray(origin: THREE.Vector3, direction: THREE.Vector3, intensity = 1, delta = 1 / 60): void {
    const safeIntensity = THREE.MathUtils.clamp(intensity, 0, 1.5);
    this.sprayAccumulator += delta * 72 * safeIntensity;
    const count = Math.min(8, Math.floor(this.sprayAccumulator));
    this.sprayAccumulator -= count;
    const forward = direction.clone().normalize();
    const right = new THREE.Vector3().crossVectors(forward, THREE.Object3D.DEFAULT_UP).normalize();
    const jetLength = 5.2;
    this.sprayJet.visible = true;
    this.sprayJetLife = 0.09;
    this.sprayJet.position.copy(origin).addScaledVector(forward, jetLength * 0.5);
    this.jetQuaternion.setFromUnitVectors(THREE.Object3D.DEFAULT_UP, forward);
    this.sprayJet.quaternion.copy(this.jetQuaternion);
    this.sprayJet.scale.set(1, jetLength, 1);
    this.sprayJetMaterial.opacity = 0.38 + safeIntensity * 0.12;

    for (let index = 0; index < count; index += 1) {
      const particle = this.dropletParticles[this.nextDroplet];
      this.nextDroplet = (this.nextDroplet + 1) % this.dropletParticles.length;
      const spread = Math.sin((this.nextDroplet + index) * 2.399963);
      particle.active = true;
      particle.position.copy(origin).addScaledVector(right, spread * 0.08);
      particle.velocity.copy(forward).multiplyScalar(8.5 + safeIntensity * 2.2);
      particle.velocity.y += 1.15 + Math.cos(this.nextDroplet * 1.7) * 0.45;
      particle.life = particle.maxLife = 0.62 + (this.nextDroplet % 5) * 0.035;
      particle.size = 0.7 + safeIntensity * 0.36;
      particle.color.set(0x77dce4);
    }
  }

  emitInteraction(position: THREE.Vector3, effect: InteractionEffect, normal = THREE.Object3D.DEFAULT_UP): void {
    const config = effectConfig(effect);
    for (let index = 0; index < config.count; index += 1) {
      const particle = this.burstParticles[this.nextBurst];
      this.nextBurst = (this.nextBurst + 1) % this.burstParticles.length;
      const angle = index * 2.399963;
      const radial = new THREE.Vector3(Math.cos(angle), 0.35 + (index % 4) * 0.16, Math.sin(angle));
      radial.addScaledVector(normal, config.normalLift).normalize();
      particle.active = true;
      particle.position.copy(position);
      particle.velocity.copy(radial).multiplyScalar(config.speed * (0.8 + (index % 3) * 0.16));
      particle.life = particle.maxLife = config.life * (0.84 + (index % 4) * 0.06);
      particle.size = config.size;
      particle.color.copy(config.color);
    }
  }

  update(delta: number): void {
    this.sprayJetLife = Math.max(0, this.sprayJetLife - delta);
    this.sprayJet.visible = this.sprayJetLife > 0;
    this.updatePool(this.droplets, this.dropletParticles, delta, 5.8);
    this.updatePool(this.bursts, this.burstParticles, delta, 3.4);
  }

  reset(): void {
    this.sprayJetLife = 0;
    this.sprayJet.visible = false;
    for (const particle of this.dropletParticles) particle.active = false;
    for (const particle of this.burstParticles) particle.active = false;
    this.hideInactiveInstances(this.droplets, this.dropletParticles);
    this.hideInactiveInstances(this.bursts, this.burstParticles);
  }

  dispose(): void {
    this.dropletGeometry.dispose();
    this.burstGeometry.dispose();
    this.sprayJetGeometry.dispose();
    this.dropletMaterial.dispose();
    this.sprayJetMaterial.dispose();
    this.burstMaterial.dispose();
    this.root.clear();
  }

  private updatePool(mesh: THREE.InstancedMesh, particles: Particle[], delta: number, gravity: number): void {
    particles.forEach((particle, index) => {
      if (particle.active) {
        particle.life -= delta;
        if (particle.life <= 0 || particle.position.y < -1.2) {
          particle.active = false;
        } else {
          particle.velocity.y -= gravity * delta;
          particle.position.addScaledVector(particle.velocity, delta);
        }
      }

      const ratio = particle.active ? THREE.MathUtils.clamp(particle.life / particle.maxLife, 0, 1) : 0;
      this.scale.setScalar(particle.size * Math.sin(Math.min(1, ratio) * Math.PI) * ratio);
      this.matrix.compose(particle.position, this.quaternion, this.scale);
      mesh.setMatrixAt(index, this.matrix);
      if (mesh === this.bursts) mesh.setColorAt(index, particle.color);
    });
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
  }

  private hideInactiveInstances(mesh: THREE.InstancedMesh, particles: Particle[]): void {
    this.scale.setScalar(0);
    particles.forEach((particle, index) => {
      this.matrix.compose(particle.position, this.quaternion, this.scale);
      mesh.setMatrixAt(index, this.matrix);
    });
    mesh.instanceMatrix.needsUpdate = true;
  }
}

function createParticlePool(count: number): Particle[] {
  return Array.from({ length: count }, () => ({
    active: false,
    position: new THREE.Vector3(),
    velocity: new THREE.Vector3(),
    life: 0,
    maxLife: 1,
    size: 1,
    color: new THREE.Color(0xffffff),
  }));
}

function effectConfig(effect: InteractionEffect): {
  color: THREE.Color;
  count: number;
  speed: number;
  life: number;
  size: number;
  normalLift: number;
} {
  switch (effect) {
    case 'water-impact':
      return { color: new THREE.Color(0x72dce4), count: 10, speed: 2.8, life: 0.7, size: 0.85, normalLift: 0.72 };
    case 'revive':
      return { color: new THREE.Color(0xe8d675), count: 18, speed: 3.1, life: 1, size: 1.15, normalLift: 0.6 };
    case 'eat':
      return { color: new THREE.Color(0xf0ad43), count: 8, speed: 1.8, life: 0.55, size: 0.72, normalLift: 0.9 };
    case 'pickup':
      return { color: new THREE.Color(0xf3ba47), count: 9, speed: 2.2, life: 0.62, size: 0.82, normalLift: 0.72 };
    case 'throw-impact':
      return { color: new THREE.Color(0xc7aa79), count: 12, speed: 3.8, life: 0.64, size: 0.9, normalLift: 0.35 };
    case 'thorn-hit':
      return { color: new THREE.Color(0xcf6744), count: 8, speed: 2.7, life: 0.52, size: 0.76, normalLift: 0.5 };
  }
}
